import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  LogOut, Plus, Trash2, Edit2, Save, X, Upload, Mail, Phone, User,
  BarChart3, Settings, Cloud, Calculator, Server, Code, BookOpen, Briefcase
} from 'lucide-react';
import {
  getServices, createService, updateService, deleteService,
  getTestimonials, createTestimonial, updateTestimonial, deleteTestimonial,
  getTeam, createTeamMember, updateTeamMember, deleteTeamMember,
  getPricing, createPricing, updatePricing, deletePricing,
  getClients, createClient, deleteClient,
  getBrochures, uploadBrochure, deleteBrochure,
  getContacts, deleteContact
} from '../lib/api';

const ICON_OPTIONS = ['BarChart3', 'Settings', 'Cloud', 'Calculator', 'Server', 'Code', 'BookOpen', 'Briefcase'];

function AdminSection({ title, children }) {
  return (
    <div className="space-y-4">
      <h3 className="font-heading font-semibold text-lg text-[#1F1F1F]">{title}</h3>
      {children}
    </div>
  );
}

// ---- Services Tab ----
function ServicesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', icon: 'BookOpen' });

  const load = useCallback(() => { getServices().then(setItems); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title) return;
    await createService(form);
    setForm({ title: '', description: '', icon: 'BookOpen' });
    load();
  };

  const handleUpdate = async (id) => {
    await updateService(id, form);
    setEditing(null);
    load();
  };

  const handleDelete = async (id) => {
    await deleteService(id);
    load();
  };

  return (
    <AdminSection title="Manage Services">
      <div className="grid gap-3">
        {items.map(item => (
          <div key={item.id} className="flex items-center gap-3 p-3 bg-white border border-[#E1E7EF] rounded-md">
            {editing === item.id ? (
              <>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-md flex-1 border-[#E1E7EF]" placeholder="Title" />
                <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-md flex-1 border-[#E1E7EF]" placeholder="Description" />
                <select value={form.icon} onChange={e => setForm({...form, icon: e.target.value})} className="border border-[#E1E7EF] rounded-md px-2 py-1 text-sm">
                  {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
                <button onClick={() => handleUpdate(item.id)} className="p-2 bg-[#0056D2] text-white rounded-md hover:bg-[#003E99] transition-colors"><Save className="w-4 h-4" /></button>
                <button onClick={() => setEditing(null)} className="p-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"><X className="w-4 h-4" /></button>
              </>
            ) : (
              <>
                <span className="font-heading font-semibold text-sm flex-1">{item.title}</span>
                <span className="font-body text-xs text-[#585858] flex-1 truncate">{item.description}</span>
                <button onClick={() => { setEditing(item.id); setForm({ title: item.title, description: item.description, icon: item.icon || 'BookOpen' }); }} className="p-2 hover:bg-[#E8F0FE] rounded-md transition-colors"><Edit2 className="w-4 h-4 text-[#585858]" /></button>
                <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-md transition-colors" data-testid={`delete-service-${item.id}`}><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Service title" data-testid="new-service-title" />
        <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Description" data-testid="new-service-desc" />
        <select value={form.icon} onChange={e => setForm({...form, icon: e.target.value})} className="border border-[#E1E7EF] rounded-md px-2 py-1 text-sm" data-testid="new-service-icon">
          {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <button onClick={handleCreate} data-testid="add-service-btn" className="flex items-center gap-2 px-4 py-2 bg-[#0056D2] text-white font-body font-semibold text-xs rounded-md hover:bg-[#003E99] transition-colors whitespace-nowrap">
          <Plus className="w-4 h-4" /> ADD
        </button>
      </div>
    </AdminSection>
  );
}

// ---- Testimonials Tab ----
function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', role: '', text: '' });

  const load = useCallback(() => { getTestimonials().then(setItems); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name || !form.text) return;
    await createTestimonial(form);
    setForm({ name: '', role: '', text: '' });
    load();
  };

  return (
    <AdminSection title="Manage Testimonials">
      <div className="grid gap-3">
        {items.map(item => (
          <div key={item.id} className="p-3 border border-[#E1E7EF] bg-white rounded-md">
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-sm">{item.name}</p>
              <p className="font-mono text-xs text-[#52525B]">{item.role}</p>
              <p className="font-body text-xs text-[#52525B] truncate mt-1">"{item.text}"</p>
            </div>
            <button onClick={() => { deleteTestimonial(item.id).then(load); }} className="p-2 hover:bg-red-50 text-red-500 rounded-md transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Name" data-testid="new-testimonial-name" />
        <Input value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Role / Company" data-testid="new-testimonial-role" />
        <Textarea value={form.text} onChange={e => setForm({...form, text: e.target.value})} className="rounded-none sm:col-span-3" placeholder="Testimonial text" rows={2} data-testid="new-testimonial-text" />
      </div>
      <button onClick={handleCreate} data-testid="add-testimonial-btn" className="flex items-center gap-2 px-4 py-2 bg-[#0056D2] text-white font-body font-semibold text-xs rounded-md hover:bg-[#003E99] transition-colors mt-3">
        <Plus className="w-4 h-4" /> ADD TESTIMONIAL
      </button>
    </AdminSection>
  );
}

// ---- Team Tab ----
function TeamTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', role: '', image: '' });

  const load = useCallback(() => { getTeam().then(setItems); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name) return;
    await createTeamMember(form);
    setForm({ name: '', role: '', image: '' });
    load();
  };

  return (
    <AdminSection title="Manage Team">
      <div className="grid gap-3">
        {items.map(item => (
          <div key={item.id} className="p-3 border border-[#E1E7EF] bg-white rounded-md">
            {item.image && <img src={item.image} alt={item.name} className="w-10 h-10 object-cover grayscale" />}
            <div className="flex-1">
              <p className="font-heading font-bold text-sm">{item.name}</p>
              <p className="font-mono text-xs text-[#52525B]">{item.role}</p>
            </div>
            <button onClick={() => { deleteTeamMember(item.id).then(load); }} className="p-2 hover:bg-red-50 text-red-500 rounded-md transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Name" data-testid="new-team-name" />
        <Input value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Role" data-testid="new-team-role" />
        <Input value={form.image} onChange={e => setForm({...form, image: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Image URL" data-testid="new-team-image" />
      </div>
      <button onClick={handleCreate} data-testid="add-team-btn" className="flex items-center gap-2 px-4 py-2 bg-[#0056D2] text-white font-body font-semibold text-xs rounded-md hover:bg-[#003E99] transition-colors mt-3">
        <Plus className="w-4 h-4" /> ADD MEMBER
      </button>
    </AdminSection>
  );
}

// ---- Pricing Tab ----
function PricingTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', price: '', currency: 'INR', duration: '', features: '', highlighted: false });

  const load = useCallback(() => { getPricing().then(setItems); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title) return;
    const data = { ...form, features: form.features.split(',').map(f => f.trim()).filter(Boolean) };
    await createPricing(data);
    setForm({ title: '', price: '', currency: 'INR', duration: '', features: '', highlighted: false });
    load();
  };

  return (
    <AdminSection title="Manage Pricing">
      <div className="grid gap-3">
        {items.map(item => (
          <div key={item.id} className="p-3 border border-[#E1E7EF] bg-white rounded-md">
            <div className="flex-1">
              <p className="font-heading font-bold text-sm">{item.title} - {item.currency === 'INR' ? '\u20B9' : '$'}{item.price}</p>
              <p className="font-mono text-xs text-[#52525B]">{item.duration} {item.highlighted && '(Featured)'}</p>
            </div>
            <button onClick={() => { deletePricing(item.id).then(load); }} className="p-2 hover:bg-red-50 text-red-500 rounded-md transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Plan title" data-testid="new-pricing-title" />
        <Input value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Price (e.g. 15,000)" data-testid="new-pricing-price" />
        <Input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Duration (e.g. 1 Month)" />
        <Input value={form.features} onChange={e => setForm({...form, features: e.target.value})} className="rounded-none sm:col-span-2" placeholder="Features (comma separated)" />
        <label className="flex items-center gap-2 font-body text-sm">
          <input type="checkbox" checked={form.highlighted} onChange={e => setForm({...form, highlighted: e.target.checked})} />
          Featured plan
        </label>
      </div>
      <button onClick={handleCreate} data-testid="add-pricing-btn" className="flex items-center gap-2 px-4 py-2 bg-[#0056D2] text-white font-body font-semibold text-xs rounded-md hover:bg-[#003E99] transition-colors mt-3">
        <Plus className="w-4 h-4" /> ADD PLAN
      </button>
    </AdminSection>
  );
}

// ---- Clients Tab ----
function ClientsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', logo_url: '' });

  const load = useCallback(() => { getClients().then(setItems); }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name) return;
    await createClient(form);
    setForm({ name: '', logo_url: '' });
    load();
  };

  return (
    <AdminSection title="Manage Clients">
      <div className="grid gap-3">
        {items.map(item => (
          <div key={item.id} className="p-3 border border-[#E1E7EF] bg-white rounded-md">
            <span className="font-heading font-bold text-sm flex-1">{item.name}</span>
            <button onClick={() => { deleteClient(item.id).then(load); }} className="p-2 hover:bg-red-50 text-red-500 rounded-md transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Client name" data-testid="new-client-name" />
        <Input value={form.logo_url} onChange={e => setForm({...form, logo_url: e.target.value})} className="rounded-md border-[#E1E7EF]" placeholder="Logo URL (optional)" />
        <button onClick={handleCreate} data-testid="add-client-btn" className="flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white font-heading font-bold text-xs tracking-tight hover:bg-white hover:text-[#0A0A0A] border border-[#0A0A0A] transition-colors whitespace-nowrap">
          <Plus className="w-4 h-4" /> ADD
        </button>
      </div>
    </AdminSection>
  );
}

// ---- Brochures Tab ----
function BrochuresTab() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => { getBrochures().then(setItems); }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!title || !file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', desc);
    formData.append('file', file);
    try {
      await uploadBrochure(formData);
      setTitle(''); setDesc(''); setFile(null);
      load();
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminSection title="Manage Brochures">
      <div className="grid gap-3">
        {items.map(item => (
          <div key={item.id} className="p-3 border border-[#E1E7EF] bg-white rounded-md">
            <div className="flex-1">
              <p className="font-heading font-bold text-sm">{item.title}</p>
              <p className="font-mono text-xs text-[#52525B]">{item.filename}</p>
            </div>
            <button onClick={() => { deleteBrochure(item.id).then(load); }} className="p-2 hover:bg-red-50 text-red-500 rounded-md transition-colors"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-md border-[#E1E7EF]" placeholder="Brochure title" data-testid="new-brochure-title" />
        <Input value={desc} onChange={e => setDesc(e.target.value)} className="rounded-md border-[#E1E7EF]" placeholder="Description (optional)" />
        <div className="sm:col-span-2">
          <label className="flex items-center gap-3 p-4 border border-dashed border-black/20 cursor-pointer hover:bg-[#F3F4F6] transition-colors">
            <Upload className="w-5 h-5 text-[#52525B]" />
            <span className="font-body text-sm text-[#52525B]">{file ? file.name : 'Choose file (PDF, DOC, etc.)'}</span>
            <input type="file" className="hidden" onChange={e => setFile(e.target.files[0])} data-testid="brochure-file-input" />
          </label>
        </div>
      </div>
      <button
        onClick={handleUpload}
        disabled={uploading || !title || !file}
        data-testid="upload-brochure-btn"
        className="flex items-center gap-2 px-4 py-2 bg-[#0056D2] text-white font-body font-semibold text-xs rounded-md hover:bg-[#003E99] transition-colors mt-3 disabled:opacity-50"
      >
        <Upload className="w-4 h-4" /> {uploading ? 'UPLOADING...' : 'UPLOAD BROCHURE'}
      </button>
    </AdminSection>
  );
}

// ---- Contacts Tab ----
function ContactsTab() {
  const [items, setItems] = useState([]);

  const load = useCallback(() => { getContacts().then(setItems); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <AdminSection title="Contact Submissions">
      {items.length === 0 ? (
        <p className="font-body text-sm text-[#52525B]">No contact submissions yet.</p>
      ) : (
        <div className="grid gap-3">
          {items.map(item => (
            <div key={item.id} className="p-4 border border-black/10 bg-white">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-[#52525B]" />
                    <span className="font-heading font-bold text-sm">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-[#52525B]" />
                    <span className="font-body text-xs text-[#52525B]">{item.email}</span>
                  </div>
                  {item.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-[#52525B]" />
                      <span className="font-body text-xs text-[#52525B]">{item.phone}</span>
                    </div>
                  )}
                  <p className="font-body text-sm text-[#0A0A0A] mt-2">{item.message}</p>
                  <p className="font-mono text-xs text-[#52525B] mt-2">{new Date(item.created_at).toLocaleString()}</p>
                </div>
                <button onClick={() => { deleteContact(item.id).then(load); }} className="p-2 hover:bg-red-50 text-red-500 rounded-md transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminSection>
  );
}

// ---- Main Admin Dashboard ----
export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login');
    }
  }, [user, loading, navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center font-mono text-sm">Loading...</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E1E7EF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="font-heading font-bold text-lg text-[#0056D2]">
              A7 Globetech
            </button>
            <span className="font-body text-xs font-semibold text-[#585858] border-l border-[#E1E7EF] pl-4">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-[#585858] hidden sm:inline">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate('/admin/login'); }}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 px-4 py-2 font-body font-semibold text-xs bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        <Tabs defaultValue="services" className="w-full">
          <TabsList className="w-full flex flex-wrap gap-1 bg-white border border-[#E1E7EF] p-1.5 mb-8 rounded-lg shadow-sm h-auto" data-testid="admin-tabs">
            {['Services', 'Testimonials', 'Team', 'Pricing', 'Clients', 'Brochures', 'Contacts'].map(tab => (
              <TabsTrigger
                key={tab}
                value={tab.toLowerCase()}
                className="font-body font-medium text-xs px-4 py-2 rounded-md data-[state=active]:bg-[#0056D2] data-[state=active]:text-white"
                data-testid={`admin-tab-${tab.toLowerCase()}`}
              >
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="services"><ServicesTab /></TabsContent>
          <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
          <TabsContent value="team"><TeamTab /></TabsContent>
          <TabsContent value="pricing"><PricingTab /></TabsContent>
          <TabsContent value="clients"><ClientsTab /></TabsContent>
          <TabsContent value="brochures"><BrochuresTab /></TabsContent>
          <TabsContent value="contacts"><ContactsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
